import React, { useRef, useCallback, useState } from 'react';
import { MentionTextareaProps, MentionItem } from './types';
import { ContextMenu } from './ContextMenu';
import { NotionPageCard } from './ActionCards/NotionPageCard';
import { useMentionDetection } from './hooks/useMentionDetection';
import { useKeyboardNavigation } from './hooks/useKeyboardNavigation';
import { insertMention, filterItems, formatMentionWithSource, createMentionSelectData } from './utils';

export const MentionTextarea: React.FC<MentionTextareaProps> = ({
  value,
  onChange,
  mentions,
  placeholder = 'Type @ to mention...',
  className = '',
  onMentionSelect,
  theme = 'auto',
  maxMenuHeight = 300,
  disabled = false,
  rows = 4,
  autoFocus = false,
  showSource = true,
  sourceFormat = 'label',
  sourceSeparator = ':',
  pathSeparator = ':'
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showActionCard, setShowActionCard] = useState<MentionItem | null>(null);
  const [cursorPosition, setCursorPosition] = useState(0);

  const {
    menuState,
    detectMention,
    closeMenu,
    updateSearch,
    navigateToLayer,
    navigateBack,
    setSelectedIndex
  } = useMentionDetection(textareaRef, mentions);

  const currentItems = menuState.layerStack[menuState.currentLayer] || [];
  const filteredItems = filterItems(currentItems, menuState.searchQuery);

  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const newCursorPosition = e.target.selectionStart;

    onChange(newValue);
    setCursorPosition(newCursorPosition);
    detectMention(newValue, newCursorPosition);
  }, [onChange, detectMention]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    setCursorPosition(e.currentTarget.selectionStart);
  }, []);

  const handleSelect = useCallback((item: MentionItem) => {
    if (item.children && item.children.length > 0) {
      // Navigate to sub-menu and track source path
      navigateToLayer(item.children, item);
      return;
    }

    if (item.component === 'NotionPageCard') {
      // Show action card
      setShowActionCard(item);
      return;
    }

    // Handle regular mention selection with source information
    const displayText = formatMentionWithSource(
      item,
      menuState.sourcePath,
      menuState.sourceIds,
      {
        showSource,
        sourceFormat,
        sourceSeparator,
        pathSeparator
      }
    );

    const { newText, newCursorPosition } = insertMention(
      value,
      displayText,
      menuState.triggerPosition,
      cursorPosition
    );

    onChange(newText);
    closeMenu();

    // Set cursor position after insertion
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
        textareaRef.current.focus();
      }
    }, 0);

    // Call the enhanced callback with structured data
    if (onMentionSelect) {
      const mentionData = createMentionSelectData(
        item,
        menuState.sourcePath,
        menuState.sourceIds,
        menuState.triggerChar,
        displayText
      );
      onMentionSelect(mentionData);
    }
  }, [
    value,
    cursorPosition,
    menuState.triggerPosition,
    menuState.sourcePath,
    menuState.sourceIds,
    menuState.triggerChar,
    onChange,
    closeMenu,
    onMentionSelect,
    navigateToLayer,
    showSource,
    sourceFormat,
    sourceSeparator,
    pathSeparator
  ]);

  const handleActionCardSubmit = useCallback((result: string) => {
    const { newText, newCursorPosition } = insertMention(
      value,
      result,
      menuState.triggerPosition,
      cursorPosition
    );

    onChange(newText);
    setShowActionCard(null);
    closeMenu();

    // Set cursor position after insertion
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
        textareaRef.current.focus();
      }
    }, 0);
  }, [value, cursorPosition, menuState.triggerPosition, onChange, closeMenu]);

  const handleActionCardCancel = useCallback(() => {
    setShowActionCard(null);
    closeMenu();

    // Return focus to textarea
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, 0);
  }, [closeMenu]);

  useKeyboardNavigation({
    isMenuOpen: menuState.isOpen && !showActionCard,
    items: filteredItems,
    selectedIndex: menuState.selectedIndex,
    onSelect: handleSelect,
    onClose: closeMenu,
    onNavigateBack: menuState.currentLayer > 0 ? navigateBack : undefined,
    canNavigateBack: menuState.currentLayer > 0,
    setSelectedIndex
  });

  const baseTextareaClasses = `
    w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
    bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
    placeholder-gray-500 dark:placeholder-gray-400
    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
    resize-none transition-colors
    ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
  `.trim();

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleTextChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={`${baseTextareaClasses} ${className}`}
        disabled={disabled}
        rows={rows}
        autoFocus={autoFocus}
      />

      {/* Context Menu */}
      <ContextMenu
        isOpen={menuState.isOpen && !showActionCard}
        position={menuState.position}
        items={filteredItems}
        selectedIndex={menuState.selectedIndex}
        searchQuery={menuState.searchQuery}
        onSelect={handleSelect}
        onClose={closeMenu}
        onSearch={updateSearch}
        onNavigateBack={menuState.currentLayer > 0 ? navigateBack : undefined}
        canNavigateBack={menuState.currentLayer > 0}
        maxHeight={maxMenuHeight}
        theme={theme}
      />

      {/* Action Card */}
      {showActionCard && (
        <div
          className="fixed z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg"
          style={{
            left: menuState.position.x,
            top: menuState.position.y
          }}
        >
          {showActionCard.component === 'NotionPageCard' && (
            <NotionPageCard
              item={showActionCard}
              onSubmit={handleActionCardSubmit}
              onCancel={handleActionCardCancel}
            />
          )}
        </div>
      )}
    </div>
  );
};
